import React from 'react';
import { Angle } from '../../../atoms/angle/angle.atom';
import * as S from './payment-form.organism.style';
import { MetodoPagamentoVendaEnum } from '../../../../common/types/enums/metodo-pagamento.enum';
import { Checkout } from '../../../../common/types/checkout';
import { MoedaVendaEnum } from '../../../../common/types/enums/moeda-venda.enum';
import { MpCardSectionModern } from '../../../molecules/mp-card-section/mp-card-section-modern';
import { MpCardSectionRef } from '../../../molecules/mp-card-section/mp-card-section';
import { FormDataShape } from '../../../../common/hooks/useCheckoutController/useCheckoutController.types';
import { Trans, useTranslation } from 'react-i18next';
import { PaisMercadoGlobalEnum } from '../../../../common/types/enums/pais-mercado-global.enum';
import { InputField } from '../../../molecules/input-field/input-field.molecule';
import { documentMasks } from '../../../../common/utils/document-masks';
import { GiCheckMark } from 'react-icons/gi';
import { StripeCardSectionModern } from '../../../molecules/stripe-card-section/stripe-card-section-modern';
import { StripeElementRef } from '../../../atoms/stripe-inputs/stripe-card-input/stripe-card-input';
import Visa from '../../.././../common/assets/visa.png';
import MasterCard from '../../.././../common/assets/mastercard.png';
import Amex from '../../.././../common/assets/amex.png';
import Discover from '../../.././../common/assets/discover.png';
import Pix from '../../../../common/assets/pix.png';
import { IoMdBarcode } from 'react-icons/io';
import { ConversionBlock } from '../../conversion-block/conversion-block.organism';

type Props = {
  selectedMethod: MetodoPagamentoVendaEnum;
  setSelectedMethod: (method: MetodoPagamentoVendaEnum) => void;
  checkout: Checkout;
  mpCardRef?: React.RefObject<MpCardSectionRef>;
  formData: FormDataShape;
  onFormDataChange: (field: keyof FormDataShape, value: string) => void;
  handlePurchaseClick: () => void;
  isLoading: boolean;
  numberRef: React.RefObject<StripeElementRef>;
  expiryRef: React.RefObject<StripeElementRef>;
  cvcRef: React.RefObject<StripeElementRef>;
  handleStripeChange: (
    field: 'number' | 'expiry' | 'cvc',
    s: { complete: boolean; error: string | null },
  ) => void;
};

export const PaymentFormModern: React.FC<Props> = ({
  selectedMethod,
  setSelectedMethod,
  checkout,
  mpCardRef,
  formData,
  onFormDataChange,
  handlePurchaseClick,
  isLoading,
  numberRef,
  expiryRef,
  cvcRef,
  handleStripeChange,
}) => {
  const { t } = useTranslation();

  const handlePlaceOrder = () => {
    if (!isLoading) {
      handlePurchaseClick();
    }
  };
  const getPurchaseButtonLabel = checkout?.recorrencia
    ? t('getInstantAccess')
    : t('purchase');

  return (
    <>
      <S.PaymentFieldset>
        <legend>{t('payment')}</legend>

        <div className="acc">
          {/* CARTÃO */}
          <div>
            {checkout?.metodosPagamento?.includes('cc') && (
              <S.AccHeader
                type="button"
                active={
                  selectedMethod === MetodoPagamentoVendaEnum.CARTAO_CREDITO
                }
                onClick={() =>
                  setSelectedMethod(MetodoPagamentoVendaEnum.CARTAO_CREDITO)
                }
              >
                <div className="left">
                  <span className="dot" />
                  <span className="method-label">{t('creditCard')}</span>
                  <S.PayLogos>
                    <img src={Visa} alt="Visa" />
                    <img src={MasterCard} alt="Mastercard" />
                    <img src={Amex} alt="Amex" />
                    <img src={Discover} alt="Discover" />
                  </S.PayLogos>
                </div>
                <Angle
                  open={
                    selectedMethod === MetodoPagamentoVendaEnum.CARTAO_CREDITO
                  }
                >
                  ▾
                </Angle>
              </S.AccHeader>
            )}
            {selectedMethod === MetodoPagamentoVendaEnum.CARTAO_CREDITO && (
              <S.AccBody>
                {checkout?.moeda === MoedaVendaEnum.BRL &&
                !checkout?.recorrencia ? (
                  <MpCardSectionModern
                    checkout={checkout}
                    data={formData}
                    onChange={onFormDataChange}
                    ref={mpCardRef}
                  />
                ) : (
                  <StripeCardSectionModern
                    checkout={checkout}
                    data={formData}
                    onChange={onFormDataChange}
                    numberRef={numberRef}
                    expiryRef={expiryRef}
                    cvcRef={cvcRef}
                    handleStripeChange={handleStripeChange}
                  />
                )}
                <ConversionBlock checkout={checkout} />
                <S.PlaceOrder
                  color={
                    checkout?.checkoutConfig?.config?.checkoutData
                      ?.purchaseButton?.color
                  }
                  background={
                    checkout?.checkoutConfig?.config?.checkoutData
                      ?.purchaseButton?.background
                  }
                  enabled={!isLoading}
                  onClick={handlePlaceOrder}
                >
                  {isLoading ? 'Processing...' : getPurchaseButtonLabel}
                </S.PlaceOrder>
              </S.AccBody>
            )}
          </div>

          {/* PIX */}
          <div>
            {checkout?.paisEnum === PaisMercadoGlobalEnum.BRA &&
              checkout?.metodosPagamento?.includes('pix') && (
                <S.AccHeader
                  type="button"
                  active={selectedMethod === MetodoPagamentoVendaEnum.PIX}
                  onClick={() =>
                    setSelectedMethod(MetodoPagamentoVendaEnum.PIX)
                  }
                >
                  <div className="left">
                    <span className="dot" />
                    <span className="method-label">{t('paymentMethodDescription.PIX')}</span>
                    <S.PayLogos>
                      <img
                        style={{ width: '40px', height: '40px' }}
                        src={Pix}
                        alt="Pix"
                      />
                    </S.PayLogos>
                  </div>
                  <Angle open={selectedMethod === MetodoPagamentoVendaEnum.PIX}>
                    ▾
                  </Angle>
                </S.AccHeader>
              )}
            {selectedMethod === MetodoPagamentoVendaEnum.PIX && (
              <S.AccBody>
                <S.PixInfo>
                  <S.InfoItem>
                    <GiCheckMark /> {t('immediateRelease')}
                  </S.InfoItem>
                  <S.InfoItem>
                    <GiCheckMark /> {t('pixPaymentInstruction')}
                  </S.InfoItem>
                </S.PixInfo>

                <div style={{ marginTop: 8 }}>
                  <InputField
                    color={
                      checkout?.checkoutConfig?.config?.checkoutData
                        ?.purchaseButton?.background
                    }
                    id="cpf"
                    label={t('document')}
                    placeholder={t('fillYourDocument')}
                    mask={documentMasks[checkout?.paisEnum]}
                    maskChar=""
                    value={formData.cpf}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      onFormDataChange('cpf', e.target.value)
                    }
                  />
                </div>

                <ConversionBlock checkout={checkout} />

                <S.PlaceOrder
                  color={
                    checkout?.checkoutConfig?.config?.checkoutData
                      ?.purchaseButton?.color
                  }
                  background={
                    checkout?.checkoutConfig?.config?.checkoutData
                      ?.purchaseButton?.background
                  }
                  enabled={!isLoading}
                  onClick={handlePlaceOrder}
                >
                  {isLoading ? 'Processing...' : getPurchaseButtonLabel}
                </S.PlaceOrder>
              </S.AccBody>
            )}
          </div>

          {/* BOLETO */}
          <div>
            {checkout?.paisEnum === PaisMercadoGlobalEnum.BRA &&
              checkout?.metodosPagamento?.includes('boleto') && (
                <S.AccHeader
                  type="button"
                  active={selectedMethod === MetodoPagamentoVendaEnum.BOLETO}
                  onClick={() =>
                    setSelectedMethod(MetodoPagamentoVendaEnum.BOLETO)
                  }
                >
                  <div className="left">
                    <span className="dot" />
                    <span className="method-label">{t('paymentMethodDescription.BOLETO')}</span>
                    <S.PayLogos>
                      <IoMdBarcode size={22} />
                    </S.PayLogos>
                  </div>
                  <Angle
                    open={selectedMethod === MetodoPagamentoVendaEnum.BOLETO}
                  >
                    ▾
                  </Angle>
                </S.AccHeader>
              )}
            {selectedMethod === MetodoPagamentoVendaEnum.BOLETO && (
              <S.AccBody>
                <S.PixInfo>
                  <S.InfoItem>
                    <GiCheckMark /> {t('availableForPayment')}
                  </S.InfoItem>
                  <S.InfoItem>
                    <GiCheckMark /> {t('clearanceTimeTwoDays')}
                  </S.InfoItem>
                </S.PixInfo>

                <div style={{ marginTop: 8 }}>
                  <InputField
                    color={
                      checkout?.checkoutConfig?.config?.checkoutData
                        ?.purchaseButton?.background
                    }
                    id="cpf"
                    label={t('document')}
                    placeholder={t('fillYourDocument')}
                    mask={documentMasks[checkout?.paisEnum]}
                    maskChar=""
                    value={formData.cpf}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      onFormDataChange('cpf', e.target.value)
                    }
                  />
                </div>

                <ConversionBlock checkout={checkout} />

                <S.PlaceOrder
                  color={
                    checkout?.checkoutConfig?.config?.checkoutData
                      ?.purchaseButton?.color
                  }
                  background={
                    checkout?.checkoutConfig?.config?.checkoutData
                      ?.purchaseButton?.background
                  }
                  enabled={!isLoading}
                  onClick={handlePlaceOrder}
                >
                  {isLoading ? 'Processing...' : getPurchaseButtonLabel}
                </S.PlaceOrder>
              </S.AccBody>
            )}
          </div>
        </div>
        <S.SecureCheckoutNote>🔒 {t('secureCheckout')}</S.SecureCheckoutNote>
      </S.PaymentFieldset>
    </>
  );
};
